import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useLocation } from "react-router-dom";

/**
 * Site theme (light / dark) for the public-facing site.
 *
 * Scope: every public route (homepage + all marketing / auth pages). The
 * authenticated app surfaces (`/dash`, `/m`) run their OWN theme system
 * (useDashTheme) and are left untouched here.
 *
 * Light is the default. The applied theme drives three things on <html>, all
 * kept in sync so the whole page — not just the homepage's own `--cr-*`
 * surfaces — flips together:
 *   - `data-cr-theme`  → the homepage / LandingNav / LandingFooter `--cr-*` tokens
 *   - `data-dash-theme`→ the shared shadcn light-mode token set + utility remaps
 *     in index.css (originally the dashboard's, now generalized site-wide)
 *   - the `.dark` class → Tailwind `dark:` variants
 */

export type SiteTheme = "light" | "dark";

const STORAGE_KEY = "cr.site.theme";
const DEFAULT_PREFERENCE: SiteTheme = "light";

/** The authenticated app surfaces (/dash, /m) run their own theme (useDashTheme). */
function isPublicRoute(pathname: string): boolean {
  if (pathname === "/m" || pathname.startsWith("/m/")) return false; // mobile app
  if (pathname === "/dash" || pathname.startsWith("/dash/")) return false; // dashboard
  return true;
}

function readStoredPreference(): SiteTheme {
  if (typeof window === "undefined") return DEFAULT_PREFERENCE;
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return v === "dark" || v === "light" ? v : DEFAULT_PREFERENCE;
  } catch {
    return DEFAULT_PREFERENCE;
  }
}

interface SiteThemeContextValue {
  /** The theme applied to the current public route (light default). */
  theme: SiteTheme;
  /** The visitor's remembered choice. */
  preference: SiteTheme;
  /** True when the current route participates in the site theme. */
  isThemed: boolean;
  /** Flip the remembered preference. */
  toggle: () => void;
}

const SiteThemeContext = createContext<SiteThemeContextValue>({
  theme: DEFAULT_PREFERENCE,
  preference: DEFAULT_PREFERENCE,
  isThemed: false,
  toggle: () => {},
});

export function SiteThemeProvider({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();
  const [preference, setPreference] = useState<SiteTheme>(readStoredPreference);

  const isPublic = isPublicRoute(pathname);
  const theme: SiteTheme = preference;
  const isThemed = isPublic;

  useEffect(() => {
    if (typeof document === "undefined") return;
    // On the app surfaces (/dash, /m) useDashTheme owns the root attributes and
    // the .dark class — don't fight it.
    if (!isPublic) return;
    const root = document.documentElement;
    root.setAttribute("data-cr-theme", theme);
    root.setAttribute("data-dash-theme", theme);
    root.classList.toggle("dark", theme === "dark");
  }, [theme, isPublic]);

  const toggle = useCallback(() => {
    setPreference((prev) => {
      const next: SiteTheme = prev === "light" ? "dark" : "light";
      try {
        window.localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* storage blocked (private mode) — keep the in-memory preference */
      }
      return next;
    });
  }, []);

  const value = useMemo<SiteThemeContextValue>(
    () => ({ theme, preference, isThemed, toggle }),
    [theme, preference, isThemed, toggle],
  );

  return <SiteThemeContext.Provider value={value}>{children}</SiteThemeContext.Provider>;
}

export function useSiteTheme(): SiteThemeContextValue {
  return useContext(SiteThemeContext);
}
